import React from "react";

import { resolveAnswerLink, AnswerSource } from "../utils/answerLinks";
import { safeAnswerHref } from "../utils/answerSafety";

/**
 * A link inside a Copilot (beta) answer. Split out of CopilotTwo.tsx so it can be tested:
 * CopilotTwo imports react-markdown, which is ESM-only and does not resolve under react-scripts'
 * Jest (the same reason CitationTwo.tsx is its own file).
 *
 * ACOP-152 test finding, AC-12 — see utils/answerLinks.ts for the full story. In short: a link
 * written as `[Title](#4)` opens source 4 in the citation panel like the numbered pill does; a link
 * with nowhere to go is plain text.
 *
 * ACOP-158 (SEC-29): every other link is an anchor only when its address is `https` (see
 * utils/answerSafety.ts). The answer is written by a model that repeats whatever it was shown, so the
 * address is checked here too, where the link is drawn, whatever the renderer upstream did: a
 * `javascript:` or `data:` address, an `http:`, `mailto:` or relative one, or none at all, comes out as
 * plain text. Only the title is passed on from the markdown; nothing else the renderer hands over
 * reaches the page.
 */

type Props = {
  href?: string;
  title?: string;
  sources?: AnswerSource[];
  onCitationClick?: (source: AnswerSource) => void;
  onLinkClick?: (href: string | undefined) => void;
  children?: React.ReactNode;
};

export default function AnswerLink({ href, title, sources, onCitationClick, onLinkClick, children }: Props) {
  const target = resolveAnswerLink(href, sources);

  if (target.kind === "citation") {
    // Neither an anchor (there is no address to put in an href, and `href="#4"` is the bug) nor a
    // button (a button is always inline-block, so a long title would not wrap with the sentence and
    // would take its list bullet with it). A focusable inline element with the link role reads, wraps
    // and tabs like the other answer links; Enter opens it, as it does a link.
    const open = () => onCitationClick?.(target.source);
    return (
      <span
        role="link"
        tabIndex={0}
        className="c2-md-link c2-md-link--citation"
        title={target.source.title}
        data-testid="c2-answer-source-link"
        data-id={target.source.id}
        onClick={open}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); open(); } }}
      >
        {children}
      </span>
    );
  }

  if (target.kind === "text") return <span>{children}</span>;

  const safeHref = safeAnswerHref(target.href);
  if (!safeHref) return <span>{children}</span>;

  return (
    <a
      href={safeHref}
      title={title}
      rel="noopener noreferrer"
      className="c2-md-link"
      onClick={(e) => { e.preventDefault(); onLinkClick?.(safeHref); }}
    >
      {children}
    </a>
  );
}
